import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { apiGet } from '../../api/client'
import { handedReasonLabel } from '../../desk/handedReasons'
import { n, s } from './AutonomyPage'

type MetricsRow = Record<string, unknown>

type MetricsPage = {
  ok: boolean
  error?: string
  rows?: MetricsRow[]
  headline?: Record<string, unknown>
  handed_by_reason?: HandedReason[]
  today?: Record<string, unknown>
}

type HandedReason = { reason: string; n: number }

function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - days)
  return d.toISOString().slice(0, 10)
}

function deskMetricsQuery(dscope: string, from: string, to: string) {
  const p = new URLSearchParams({ from, to })
  if (dscope) p.set('dscope', dscope)
  return {
    queryKey: ['settings', 'autonomy', 'desk-metrics', dscope, from, to] as const,
    queryFn: () => apiGet<MetricsPage>(`/settings/autonomy/desk/metrics?${p.toString()}`),
  }
}

type TrendWeek = {
  week: string
  from: string
  taken: number
  rate: number | null
  cost_per_confirmed: number | null
}

type TrendType = {
  request_type: string
  taken: number
  rate: number | null
  cost_per_confirmed: number | null
  handed: number
  last_rate: number | null
  prev_rate: number | null
  delta: number | null
  weeks: TrendWeek[]
}

type StageGate = { key: string; ok: boolean; value?: unknown; threshold?: unknown; reason?: string }

type StageSuggestion = {
  stage: string
  next: string | null
  can_promote: boolean
  gates: StageGate[]
  red_days: number
  red_limit: number
  watch: boolean
  demote_to: string | null
  text: string
}

type Deflection = {
  days: number
  searches: number
  article: number
  cart: number
  ticket: number
  deflected: number
  rate: number | null
  citations: number
}

type TrendsPage = {
  ok: boolean
  error?: string
  trend?: { weeks: TrendWeek[]; by_type: TrendType[]; cost_per_confirmed_4w: number | null }
  stage?: StageSuggestion | null
  deflection?: Deflection
}

function deskTrendsQuery(dscope: string) {
  const p = new URLSearchParams()
  if (dscope) p.set('dscope', dscope)
  return {
    queryKey: ['settings', 'autonomy', 'desk-trends', dscope] as const,
    queryFn: () => apiGet<TrendsPage>(`/settings/autonomy/desk/trends?${p.toString()}`),
  }
}

function share(rate: number | null | undefined): string {
  return rate == null ? '—' : `${Math.round(rate * 100)}%`
}

function usd(v: number | null | undefined): string {
  return v == null ? '—' : `${v.toFixed(2)} USD`
}

/** Change of the confirmed rate against the previous week with requests, in percentage points. */
function DeltaCell({ delta }: { delta: number | null }) {
  if (delta == null) return <span className="text-muted">—</span>
  const pts = Math.round(delta * 100)
  const tone = pts > 0 ? 'text-success' : pts < 0 ? 'text-danger' : 'text-muted'
  return (
    <span className={tone} title={`${delta > 0 ? '+' : ''}${delta.toFixed(2)}`}>
      {pts > 0 ? '+' : ''}
      {pts} pts
    </span>
  )
}

function RateLine({ weeks, width, height, label }: { weeks: TrendWeek[]; width: number; height: number; label: string }) {
  const step = weeks.length > 1 ? width / (weeks.length - 1) : 0
  const points = weeks
    .map((w, i) => (w.rate == null ? null : `${Math.round(i * step)},${Math.round(height - w.rate * height)}`))
    .filter((p): p is string => p !== null)
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label} style={{ overflow: 'visible', maxWidth: '100%', height: 'auto' }}>
      <line x1={0} y1={height} x2={width} y2={height} stroke="#ddd" />
      {points.length > 1 && <polyline points={points.join(' ')} fill="none" stroke="#0b6e99" strokeWidth={1.5} />}
      {points.map((p) => {
        const [x, y] = p.split(',')
        return <circle key={p} cx={x} cy={y} r={2} fill="#0b6e99" />
      })}
    </svg>
  )
}

function DeskTrends({ dscope }: { dscope: string }) {
  const q = useQuery(deskTrendsQuery(dscope))
  if (q.isLoading) return <div className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>Loading the 8-week trends…</div>
  if (!q.data?.ok) {
    return (
      <div className="text-danger" style={{ fontSize: 12, marginTop: 8 }}>
        {q.data?.error || 'The 8-week trends did not load. Reload the page to try again.'}
      </div>
    )
  }
  const trend = q.data.trend
  const stage = q.data.stage
  const defl = q.data.deflection
  const canPromote = !!stage && stage.can_promote && !stage.watch && stage.gates.length > 0 && stage.gates.every((g) => g.ok)
  return (
    <div id="odsk-trends" style={{ marginTop: 14 }}>
      <strong style={{ fontSize: 13 }}>8 weeks by type</strong>
      <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
        confirmed rate = (confirmed + held) / taken; silence is not counted here
        {trend?.cost_per_confirmed_4w != null ? ` · cost per confirmed resolution, last 4 weeks: ${usd(trend.cost_per_confirmed_4w)}` : ''}
      </span>
      {trend && trend.weeks.some((w) => w.taken > 0) && (
        <div style={{ marginTop: 8 }}>
          <RateLine weeks={trend.weeks} width={320} height={60} label="Confirmed rate per week, all types" />
          <div className="text-muted" style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', width: 320, maxWidth: '100%' }}>
            <span>{trend.weeks[0]?.week}</span>
            <span>{trend.weeks[trend.weeks.length - 1]?.week}</span>
          </div>
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-condensed" style={{ fontSize: 12, marginTop: 8 }}>
          <thead>
            <tr>
              <th>Request type</th>
              <th style={{ textAlign: 'right' }}>Requests</th>
              <th style={{ textAlign: 'right' }}>Confirmed rate</th>
              <th style={{ textAlign: 'right' }}>Last week</th>
              <th style={{ textAlign: 'right' }}>Change</th>
              <th>8 weeks</th>
              <th style={{ textAlign: 'right' }}>Cost / confirmed</th>
              <th style={{ textAlign: 'right' }}>Handed over</th>
            </tr>
          </thead>
          <tbody>
            {!trend || trend.by_type.length === 0 ? (
              <tr><td colSpan={8} className="text-muted">No rolled-up days in the last 8 weeks yet.</td></tr>
            ) : (
              trend.by_type.map((t) => (
                <tr key={t.request_type} data-type={t.request_type}>
                  <td><code>{t.request_type}</code></td>
                  <td style={{ textAlign: 'right' }}>{t.taken}</td>
                  <td style={{ textAlign: 'right' }}>{share(t.rate)}</td>
                  <td style={{ textAlign: 'right' }} title={t.prev_rate != null ? `before: ${share(t.prev_rate)}` : undefined}>{share(t.last_rate)}</td>
                  <td style={{ textAlign: 'right' }}><DeltaCell delta={t.delta} /></td>
                  <td><RateLine weeks={t.weeks} width={96} height={18} label={`Confirmed rate per week, ${t.request_type}`} /></td>
                  <td style={{ textAlign: 'right' }}>{usd(t.cost_per_confirmed)}</td>
                  <td style={{ textAlign: 'right' }}>{t.handed}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {stage && (
        <div id="odsk-stage-suggestion" style={{ fontSize: 12, marginTop: 6 }}>
          <strong style={{ fontSize: 13 }}>Stage</strong>{' '}
          <code>{stage.stage}</code>
          {stage.next ? <> → <code>{stage.next}</code></> : null}{' '}
          {canPromote && <span className="label label-success">can promote</span>}
          {stage.watch && <span className="label label-danger">watch</span>}
          <div className={stage.watch ? 'text-danger' : 'text-muted'} style={{ marginTop: 4 }}>{stage.text}</div>
          {stage.gates.length > 0 && (
            <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {stage.gates.map((g) => (
                <span key={g.key} className={`label ${g.ok ? 'label-success' : 'label-default'}`} title={g.reason || undefined}>
                  {g.key}
                  {g.value != null ? `: ${String(g.value)}` : ''}
                  {g.threshold != null ? ` / ${String(g.threshold)}` : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {defl && (
        <div id="odsk-deflection" style={{ fontSize: 12, marginTop: 10 }}>
          <strong style={{ fontSize: 13 }}>Deflection</strong>
          <span className="text-muted"> · last {defl.days} days</span>
          <div style={{ marginTop: 4 }}>
            {defl.searches === 0 ? (
              <span className="text-muted">No Self-Service searches in this period.</span>
            ) : (
              <>
                {defl.searches} searches: {defl.article} read an article, {defl.cart} added to the cart, {defl.ticket} opened a ticket
                {' · '}
                <strong>{share(defl.rate)}</strong> ended without a ticket
              </>
            )}
            {' · '}
            {defl.citations} desk {defl.citations === 1 ? 'answer' : 'answers'} citing an article
          </div>
        </div>
      )}
    </div>
  )
}

function pct(rate: unknown): string {
  const v = Number(rate ?? 0)
  if (!Number.isFinite(v) || v <= 0) return '0%'
  return `${v}%`
}

function avg(a: unknown, b: unknown): string {
  const num = Number(a ?? 0)
  const den = Number(b ?? 0)
  if (den <= 0) return '—'
  return String(Math.round((num / den) * 10) / 10)
}

export function DeskMetricsPanel({ dscope }: { dscope: string }) {
  const [days, setDays] = useState(7)
  const range = useMemo(() => {
    const to = isoDaysAgo(0)
    const from = isoDaysAgo(Math.max(1, days) - 1)
    return { from, to }
  }, [days])
  const q = useQuery({ ...deskMetricsQuery(dscope, range.from, range.to), enabled: true })
  const headline = q.data?.headline ?? {}
  const rows = q.data?.rows ?? []
  const handedByReason = (q.data?.handed_by_reason ?? []).filter((r) => n(r.n) > 0)
  const byType = useMemo(() => {
    const map = new Map<string, MetricsRow>()
    for (const row of rows) {
      const key = s(row.request_type) || 'other'
      const prev = map.get(key) ?? {}
      const add = (k: string) => Number(prev[k] ?? 0) + Number(row[k] ?? 0)
      map.set(key, {
        request_type: key,
        taken: add('taken'),
        confirmed: add('confirmed'),
        held: add('held'),
        silent: add('silent'),
        reopened: add('reopened'),
        not_fixed: add('not_fixed'),
        handed: add('handed'),
        failed: add('failed'),
        rounds_sum: add('rounds_sum'),
        cost_usd: add('cost_usd'),
      })
    }
    return [...map.values()].sort((a, b) => s(a.request_type).localeCompare(s(b.request_type)))
  }, [rows])

  return (
    <div className="panel panel-default" id="odsk-metrics" style={{ marginTop: 12 }}>
      <div className="panel-body">
        <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>Desk metrics</strong>
          <span className="text-muted" style={{ fontSize: 12 }}>
            autonomous resolution rate, cost, and breakdown by request type. Today is computed live; earlier days are filled nightly.
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, fontSize: 12 }}>
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                className={`btn btn-xs ${days === d ? 'btn-primary' : 'btn-default'}`}
                onClick={() => setDays(d)}
              >
                {d} days
              </button>
            ))}
          </span>
        </div>
        {q.isLoading && <div className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>Loading…</div>}
        {(q.isError || (q.data && !q.data.ok)) && (
          <div className="text-danger" style={{ fontSize: 12, marginTop: 8 }}>
            {q.data?.error || 'The desk metrics did not load. Reload the page to try again.'}
          </div>
        )}
        {q.data?.ok && (
          <>
            <div style={{ marginTop: 10, fontSize: 13 }}>
              <strong>Autonomous resolution:</strong> {pct(headline.rate)}
              {' · '}
              <span className="text-muted">
                {n(headline.resolved)} of {n(headline.taken)} taken
                {n(headline.silent) > 0 ? ` (silent ${n(headline.silent)})` : ''}
                {' · '}
                cost / resolved {Number(headline.cost_per_resolved ?? 0).toFixed(2)} USD
                {headline.csat != null ? ` · CSAT ${Number(headline.csat).toFixed(1)}` : ''}
              </span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-condensed" style={{ fontSize: 12, marginTop: 10 }}>
                <thead>
                  <tr>
                    <th>Request type</th>
                    <th style={{ textAlign: 'right' }}>Taken</th>
                    <th style={{ textAlign: 'right' }}>Confirmed</th>
                    <th style={{ textAlign: 'right' }}>Held</th>
                    <th style={{ textAlign: 'right' }}>Silent</th>
                    <th style={{ textAlign: 'right' }}>Reopened</th>
                    <th style={{ textAlign: 'right' }}>Not fixed</th>
                    <th style={{ textAlign: 'right' }}>Handed</th>
                    <th style={{ textAlign: 'right' }}>Failed</th>
                    <th style={{ textAlign: 'right' }}>Avg rounds</th>
                    <th style={{ textAlign: 'right' }}>Cost USD</th>
                  </tr>
                </thead>
                <tbody>
                  {byType.length === 0 ? (
                    <tr><td colSpan={11} className="text-muted">No rolled-up days in this interval yet.</td></tr>
                  ) : (
                    byType.map((row) => (
                      <tr key={s(row.request_type)}>
                        <td><code>{s(row.request_type)}</code></td>
                        <td style={{ textAlign: 'right' }}>{n(row.taken)}</td>
                        <td style={{ textAlign: 'right' }}>{n(row.confirmed)}</td>
                        <td style={{ textAlign: 'right' }}>{n(row.held)}</td>
                        <td style={{ textAlign: 'right' }}>{n(row.silent)}</td>
                        <td style={{ textAlign: 'right' }}>{n(row.reopened)}</td>
                        <td style={{ textAlign: 'right' }}>{n(row.not_fixed)}</td>
                        <td style={{ textAlign: 'right' }} title="Hand-over reasons are counted below the table">{n(row.handed)}</td>
                        <td style={{ textAlign: 'right' }} title="lab_error, timeout, budget, …">{n(row.failed)}</td>
                        <td style={{ textAlign: 'right' }}>{avg(row.rounds_sum, row.taken)}</td>
                        <td style={{ textAlign: 'right' }}>{Number(row.cost_usd ?? 0).toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div id="odsk-handed-reasons" style={{ fontSize: 12 }}>
              <strong style={{ fontSize: 13 }}>Handed over, by reason</strong>
              {handedByReason.length === 0 ? (
                <span className="text-muted"> · none in this interval</span>
              ) : (
                <span>
                  {' · '}
                  {handedByReason.map((r, i) => (
                    <span key={r.reason} data-reason={r.reason}>
                      {i > 0 ? ', ' : ''}
                      {handedReasonLabel(r.reason)} {n(r.n)}
                    </span>
                  ))}
                </span>
              )}
            </div>
          </>
        )}
        <DeskTrends dscope={dscope} />
      </div>
    </div>
  )
}
